import React, { Component } from 'react';
import clsx from 'clsx';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { CssBaseline, Container, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Tooltip, Switch, FormControlLabel, Skeleton } from '@mui/material';
import { withStyles } from '@mui/styles';
import { Styles } from '../../styles/material-styles';
import CustomizedSnackbars from '../../components/material-snackbars';
import HeaderMenu from '../../components/header-menu';
import Footer from '../../components/footer';
import Api from '../../services/api';
import AppConnectorService, { normalizeEndpoint, normalizeProvider } from '../../services/appconnector.service';
import { FormattedMessage, injectIntl } from 'react-intl';
import styles from '../../styles/appconnector-styles';
import * as componentActions from '../../redux/actions/header-menu';
import { Plus, ArrowLeft, Copy, RefreshCw, Trash2, Settings, Edit, Pause, Play } from 'lucide-react';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import moment from 'moment';
import CopyableField from '../../components/app-connector/CopyableField';

const ensureHttps = url => url ? url.replace(/^http:\/\//i, 'https://') : '';

const buildEndpointUrl = guid => {
  const apiBaseUrl = Api && typeof Api.apiurlbase === 'function' ? Api.apiurlbase() : '';
  return apiBaseUrl && guid
    ? `${ensureHttps(apiBaseUrl).replace(/\/+$/, '')}/api/app-connectors/webhook/${guid}`
    : '';
};

class AppConnectorEndpoints extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      openNotification: false,
      notificationVariant: 'error',
      notificationMessage: '',
      provider: null,
      endpoints: [],
      createDialogOpen: false,
      secretDialogOpen: false,
      deleteDialogOpen: false,
      editDialogOpen: false,
      pauseDialogOpen: false,
      deleteId: null,
      editingEndpoint: null,
      pausingEndpoint: null,
      newEndpoint: { name: '', description: '', callbackApiUrl: '', callbackApiConfig: '' },
      createdSecret: null,
      createdEndpointUrl: null,
      requiresCallback: false,
    };
    this.closeNotification = this.closeNotification.bind(this);
  }

  componentDidMount() {
    this.loadAll();
  }

  loadAll = () => {
    this.setState({ loading: true });
    const { providerId } = this.props.match.params;

    Promise.all([
      AppConnectorService.getProviderById(providerId),
      AppConnectorService.getEndpoints()
    ]).then(([providerResult, endpointsResult]) => {
      let provider = null;
      let requiresCallback = false;

      if (providerResult.data.success) {
        provider = normalizeProvider(providerResult.data.data);
        try {
          const meta = JSON.parse(provider.metadata || '{}');
          requiresCallback = meta.requires_callback_enrichment === true;
        } catch {}
      }

      let endpoints = [];
      if (endpointsResult.data.success) {
        const raw = endpointsResult.data.data || [];
        const all = raw.map(normalizeEndpoint);
        const pid = parseInt(providerId);
        const filtered = all.filter(e => {
          const v = e.appConnectorProviderId ?? e.appconnectorproviderid ?? e.externalProviderId ?? e.externalproviderid;
          return v != null && parseInt(v) === pid;
        });
        endpoints = filtered;
      }

      this.setState({ loading: false, provider, requiresCallback, endpoints });
    }).catch(err => {
      this.setState({ loading: false });
      Api.kickoff(err);
    });
  }

  loadEndpoints = () => {
    const { providerId } = this.props.match.params;
    AppConnectorService.getEndpoints()
      .then(result => {
        if (result.data.success) {
          const raw = result.data.data || [];
          const all = raw.map(normalizeEndpoint);
          const pid = parseInt(providerId);
          const filtered = all.filter(e => {
            const v = e.appConnectorProviderId ?? e.appconnectorproviderid ?? e.externalProviderId ?? e.externalproviderid;
            return v != null && parseInt(v) === pid;
          });
          this.setState({ endpoints: filtered });
        }
      })
      .catch(err => Api.kickoff(err));
  }

  handleCreate = () => {
    const { providerId } = this.props.match.params;
    const data = {
      appConnectorProviderId: parseInt(providerId),
      name: this.state.newEndpoint.name,
      description: this.state.newEndpoint.description,
      callbackApiUrl: this.state.newEndpoint.callbackApiUrl || null,
      callbackApiConfig: this.state.newEndpoint.callbackApiConfig || null,
    };
    this.setState({ loading: true });
    AppConnectorService.createEndpoint(data)
      .then(result => {
        if (result.data.success) {
          const created = result.data.data;
          this.setState({
            loading: false, createDialogOpen: false,
            createdSecret: created.signingsecret || created.signingSecret,
            createdEndpointUrl: ensureHttps(created.endpointurl || created.endpointUrl) || buildEndpointUrl(created.guid),
            secretDialogOpen: true,
            newEndpoint: { name: '', description: '', callbackApiUrl: '', callbackApiConfig: '' },
          });
          this.loadEndpoints();
        } else {
          this.setState({ loading: false, openNotification: true, notificationVariant: 'error',
            notificationMessage: result.data.errors?.[0] || 'Erro ao criar endpoint' });
        }
      })
      .catch(err => {
        this.setState({ loading: false, openNotification: true, notificationVariant: 'error',
          notificationMessage: err.response?.data?.errors?.[0] || 'Erro ao criar endpoint' });
      });
  }

  handleEdit = () => {
    const ep = this.state.editingEndpoint;
    if (!ep) return;
    this.setState({ loading: true });
    AppConnectorService.updateEndpoint(ep.id, { id: ep.id, name: ep.name, description: ep.description || '', isActive: ep.isActive !== false, isPaused: ep.isPaused || false })
      .then(result => {
        if (result.data.success) {
          this.setState({ loading: false, editDialogOpen: false, editingEndpoint: null,
            openNotification: true, notificationVariant: 'success', notificationMessage: 'Endpoint atualizado com sucesso' });
          this.loadEndpoints();
        } else {
          this.setState({ loading: false, openNotification: true, notificationVariant: 'error',
            notificationMessage: result.data.errors?.[0] || 'Erro ao atualizar endpoint' });
        }
      })
      .catch(err => {
        this.setState({ loading: false, openNotification: true, notificationVariant: 'error',
          notificationMessage: err.response?.data?.errors?.[0] || 'Erro ao atualizar endpoint' });
      });
  }

  handlePauseToggle = () => {
    const ep = this.state.pausingEndpoint;
    if (!ep) return;
    this.setState({ loading: true });
    const newPaused = !ep.isPaused;
    AppConnectorService.updateEndpoint(ep.id, { id: ep.id, name: ep.name, description: ep.description || '', isActive: ep.isActive !== false, isPaused: newPaused, pauseReason: newPaused ? 'Pausado manualmente' : null })
      .then(result => {
        if (result.data.success) {
          this.setState({ loading: false, pauseDialogOpen: false, pausingEndpoint: null,
            openNotification: true, notificationVariant: 'success',
            notificationMessage: newPaused ? 'Endpoint pausado' : 'Endpoint ativado' });
          this.loadEndpoints();
        } else {
          this.setState({ loading: false, openNotification: true, notificationVariant: 'error',
            notificationMessage: result.data.errors?.[0] || 'Erro' });
        }
      })
      .catch(err => {
        this.setState({ loading: false, openNotification: true, notificationVariant: 'error',
          notificationMessage: err.response?.data?.errors?.[0] || 'Erro' });
      });
  }

  handleDelete = () => {
    this.setState({ loading: true });
    AppConnectorService.deleteEndpoint(this.state.deleteId)
      .then(result => {
        this.setState({ loading: false, deleteDialogOpen: false, deleteId: null,
          openNotification: true, notificationVariant: 'success', notificationMessage: 'Endpoint excluído com sucesso' });
        this.loadEndpoints();
      })
      .catch(err => {
        this.setState({ loading: false, openNotification: true, notificationVariant: 'error',
          notificationMessage: err.response?.data?.errors?.[0] || 'Erro ao excluir' });
      });
  }

  handleEndpointClick = (endpoint) => {
    this.props.history.push(`/${this.props.match.params.lang}/app-connectors/endpoints/${endpoint.id}`);
  }

  copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    this.setState({ openNotification: true, notificationVariant: 'success', notificationMessage: this.props.intl.formatMessage({ id: 'appconnector.common.copied' }) });
  }

  closeNotification() { this.setState({ openNotification: false }); }

  render() {
    const { classes, headerMenu, actionsHeaderMenu, intl } = this.props;
    const { provider, endpoints, createDialogOpen, secretDialogOpen, deleteDialogOpen, editDialogOpen, pauseDialogOpen, requiresCallback, editingEndpoint, pausingEndpoint } = this.state;
    const tr = (id, values) => intl.formatMessage({ id }, values);

    return (
      <div className={classes.root} style={{ paddingLeft: 0, marginLeft: 0 }}>
        <HeaderMenu />
        <main className={clsx(classes.content, { [classes.contentShift]: headerMenu.open }, classes.actionsContent, { [classes.actionscontentShift]: actionsHeaderMenu.open })}>
          <div className={classes.drawerHeader} />
          <Container component='main' maxWidth='xl' style={{ maxWidth: '100%', paddingLeft: 0, marginLeft: 0 }}>
            <CssBaseline />
            <div style={styles.contentWrapper}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <IconButton onClick={() => this.props.history.push(`/${this.props.match.params.lang}/app-connectors`)}>
                  <ArrowLeft size={20} />
                </IconButton>
                {this.state.loading ? (
                  <Skeleton variant="text" width={200} height={36} animation="wave" />
                ) : (
                  <h1 style={styles.heading}>{provider?.name || tr('appconnector.endpoints.fallback.title')}</h1>
                )}
              </div>
              {this.state.loading ? (
                <Skeleton variant="text" width={400} height={20} animation="wave" style={{ marginBottom: '16px' }} />
              ) : (
                <p style={styles.subheading}>{provider?.description}</p>
              )}

              {!this.state.loading && (
                <Button variant="contained" color="primary" startIcon={<Plus size={18} />}
                  onClick={() => this.setState({ createDialogOpen: true })}
                  sx={{ mb: 3, textTransform: 'none', borderRadius: '8px' }}>
                  {tr('appconnector.endpoints.new')}
                </Button>
              )}

              {!this.state.loading && endpoints.length > 0 && (
                <TextField
                  placeholder={tr('appconnector.endpoints.search.placeholder')}
                  size="small"
                  value={this.state.searchValue || ''}
                  onChange={e => this.setState({ searchValue: e.target.value })}
                  sx={{ mb: 2, maxWidth: 400, display: 'block' }}
                  fullWidth
                />
              )}

              {this.state.loading ? (
                [1, 2, 3].map(i => (
                  <div key={i} style={styles.endpointCard}>
                    <div style={styles.endpointCardHeader}>
                      <div>
                        <Skeleton variant="text" width={200} height={24} animation="wave" />
                        <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                          <Skeleton variant="rounded" width={60} height={24} animation="wave" />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <Skeleton variant="circular" width={32} height={32} animation="wave" />
                        <Skeleton variant="circular" width={32} height={32} animation="wave" />
                      </div>
                    </div>
                    <div style={styles.endpointStats}>
                      {[1, 2, 3].map(j => (
                        <div key={j} style={styles.statItem}>
                          <Skeleton variant="text" width={40} height={28} animation="wave" />
                          <Skeleton variant="text" width={60} height={12} animation="wave" />
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <>
                  {endpoints.filter(ep => !this.state.searchValue || ep.name.toLowerCase().includes((this.state.searchValue || '').toLowerCase())).map(ep => (
                    <div key={ep.id} style={styles.endpointCard}>
                      <div style={styles.endpointCardHeader}>
                        <div>
                          <span style={styles.endpointName} onClick={() => this.handleEndpointClick(ep)}>{ep.name}</span>
                          {ep.description && (
                            <p style={{ fontSize: '12px', color: '#888', margin: '4px 0 0', lineHeight: 1.4, maxWidth: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {ep.description}
                            </p>
                          )}
                          <div style={{ display: 'flex', gap: '8px', marginTop: '6px', alignItems: 'center' }}>
                            <Chip label={tr(ep.isActive ? 'appconnector.common.active' : 'appconnector.common.inactive')} size="small" color={ep.isActive ? 'success' : 'default'} />
                            {ep.isPaused && <Chip label={tr('appconnector.common.paused')} size="small" color="warning" />}
                            <Chip label={`#${ep.id}`} size="small" variant="outlined" sx={{ fontSize: '10px', fontFamily: 'monospace' }} />
                            {ep.lastEventReceivedAt && (
                              <span style={{ fontSize: '11px', color: '#999' }}>
                                {tr('appconnector.endpoints.last.activity')}: {moment(ep.lastEventReceivedAt).fromNow()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <Tooltip title={tr(ep.isPaused ? 'appconnector.common.activate' : 'appconnector.common.pause')}>
                            <IconButton onClick={() => this.setState({ pauseDialogOpen: true, pausingEndpoint: ep })}>
                              {ep.isPaused ? <Play size={18} color="#2e7d32" /> : <Pause size={18} color="#f57f17" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={tr('appconnector.common.edit')}><IconButton onClick={() => this.setState({ editDialogOpen: true, editingEndpoint: { ...ep } })}><Edit size={18} /></IconButton></Tooltip>
                          <Tooltip title={tr('appconnector.common.configure')}><IconButton onClick={() => this.handleEndpointClick(ep)}><Settings size={18} /></IconButton></Tooltip>
                          <Tooltip title={tr('appconnector.common.delete')}><IconButton onClick={() => this.setState({ deleteDialogOpen: true, deleteId: ep.id })}><Trash2 size={18} color="#c62828" /></IconButton></Tooltip>
                        </div>
                      </div>

                      {/* Webhook URL - destino para copiar */}
                      <div style={{ marginTop: '10px', padding: '10px 14px', background: '#f8faf5', borderRadius: '8px', border: '1px solid #e8f5e9' }}>
                        <span style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase', fontWeight: 600 }}>{tr('appconnector.endpoints.target.url.label')}: </span>
                        <div style={{ marginTop: '4px' }}>
                          <CopyableField value={ensureHttps(ep.endpointUrl) || buildEndpointUrl(ep.guid)} label={tr('appconnector.common.copy.url')} truncate={false} />
                        </div>
                      </div>

                      <div style={styles.endpointStats}>
                        <div style={styles.statItem}><span style={styles.statValue}>{ep.totalEventsReceived || 0}</span><span style={styles.statLabel}>{tr('appconnector.endpoints.stat.received')}</span></div>
                        <div style={styles.statItem}><span style={{ ...styles.statValue, color: '#2e7d32' }}>{ep.totalEventsProcessed || 0}</span><span style={styles.statLabel}>{tr('appconnector.endpoints.stat.processed')}</span></div>
                        <div style={styles.statItem}><span style={{ ...styles.statValue, color: '#c62828' }}>{ep.totalEventsFailed || 0}</span><span style={styles.statLabel}>{tr('appconnector.endpoints.stat.failed')}</span></div>
                      </div>
                    </div>
                  ))}

                  {endpoints.length === 0 && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 20px' }}>
                      <div style={{
                        maxWidth: 440, textAlign: 'center', padding: '40px',
                        background: '#fff', borderRadius: '16px',
                        border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                      }}>
                        <IntegrationInstructionsIcon sx={{ fontSize: 56, color: '#bdbdbd', mb: 2 }} />
                        <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#333', marginBottom: '8px' }}>
                          {tr('appconnector.endpoints.empty.title')}
                        </h3>
                        <p style={{ fontSize: '14px', color: '#888', lineHeight: 1.6, marginBottom: '24px' }}>
                          {tr('appconnector.endpoints.empty.description', { provider: provider?.name || tr('appconnector.endpoints.empty.fallback.provider') })}
                        </p>
                        <Button variant="contained" startIcon={<Plus size={18} />}
                          onClick={() => this.setState({ createDialogOpen: true })}
                          sx={{ textTransform: 'none', borderRadius: '8px', px: 3, py: 1 }}>
                          {tr('appconnector.endpoints.empty.cta')}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Create Dialog */}
            <Dialog open={createDialogOpen} onClose={() => this.setState({ createDialogOpen: false })} maxWidth="sm" fullWidth>
              <DialogTitle>{tr('appconnector.endpoints.dialog.new.title', { provider: provider?.name || '' })}</DialogTitle>
              <DialogContent>
                <TextField label={tr('appconnector.common.name')} fullWidth margin="normal" value={this.state.newEndpoint.name}
                  onChange={e => this.setState({ newEndpoint: { ...this.state.newEndpoint, name: e.target.value } })} required />
                <TextField label={tr('appconnector.common.description')} fullWidth margin="normal" multiline rows={2} value={this.state.newEndpoint.description}
                  onChange={e => this.setState({ newEndpoint: { ...this.state.newEndpoint, description: e.target.value } })} />
                {requiresCallback && (
                  <>
                    <TextField label={tr('appconnector.endpoints.callback.url.label')} fullWidth margin="normal" placeholder="https://empresa.exemplo.com/api"
                      helperText={tr('appconnector.endpoints.callback.url.helper')}
                      value={this.state.newEndpoint.callbackApiUrl}
                      onChange={e => this.setState({ newEndpoint: { ...this.state.newEndpoint, callbackApiUrl: e.target.value } })} />
                    <TextField label={tr('appconnector.endpoints.callback.config.label')} fullWidth margin="normal" multiline rows={3}
                      placeholder='{"client_id": "...", "client_secret": "..."}'
                      helperText={tr('appconnector.endpoints.callback.config.helper')}
                      value={this.state.newEndpoint.callbackApiConfig}
                      onChange={e => this.setState({ newEndpoint: { ...this.state.newEndpoint, callbackApiConfig: e.target.value } })} />
                  </>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => this.setState({ createDialogOpen: false })}>{tr('appconnector.common.cancel')}</Button>
                <Button variant="contained" onClick={this.handleCreate} disabled={!this.state.newEndpoint.name}>{tr('appconnector.common.create')}</Button>
              </DialogActions>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onClose={() => this.setState({ editDialogOpen: false })} maxWidth="sm" fullWidth>
              <DialogTitle>{tr('appconnector.endpoints.dialog.edit.title')}</DialogTitle>
              <DialogContent>
                {editingEndpoint && (
                  <>
                    <TextField label={tr('appconnector.common.name')} fullWidth margin="normal" value={editingEndpoint.name || ''}
                      onChange={e => this.setState({ editingEndpoint: { ...editingEndpoint, name: e.target.value } })} required />
                    <TextField label={tr('appconnector.common.description')} fullWidth margin="normal" multiline rows={2} value={editingEndpoint.description || ''}
                      onChange={e => this.setState({ editingEndpoint: { ...editingEndpoint, description: e.target.value } })} />
                  </>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => this.setState({ editDialogOpen: false })}>{tr('appconnector.common.cancel')}</Button>
                <Button variant="contained" onClick={this.handleEdit} disabled={!editingEndpoint?.name}>{tr('appconnector.common.save')}</Button>
              </DialogActions>
            </Dialog>

            {/* Pause/Activate Confirmation Dialog */}
            <Dialog open={pauseDialogOpen} onClose={() => this.setState({ pauseDialogOpen: false })}>
              <DialogTitle>{tr(pausingEndpoint?.isPaused ? 'appconnector.endpoints.dialog.activate.title' : 'appconnector.endpoints.dialog.pause.title')}</DialogTitle>
              <DialogContent>
                {tr(pausingEndpoint?.isPaused
                  ? 'appconnector.endpoints.dialog.activate.confirm'
                  : 'appconnector.endpoints.dialog.pause.confirm', { name: pausingEndpoint?.name || '' })}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => this.setState({ pauseDialogOpen: false })}>{tr('appconnector.common.cancel')}</Button>
                <Button variant="contained" color={pausingEndpoint?.isPaused ? 'success' : 'warning'} onClick={this.handlePauseToggle}>
                  {tr(pausingEndpoint?.isPaused ? 'appconnector.common.activate' : 'appconnector.common.pause')}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Secret Dialog */}
            <Dialog open={secretDialogOpen} onClose={() => this.setState({ secretDialogOpen: false })} maxWidth="sm" fullWidth>
              <DialogTitle>{tr('appconnector.endpoints.dialog.created.title')}</DialogTitle>
              <DialogContent>
                <p style={{ marginBottom: '12px', color: '#c62828', fontWeight: 500 }}>
                  {tr('appconnector.endpoints.dialog.created.warning')}
                </p>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>{tr('appconnector.endpoints.dialog.created.url.label')}</p>
                <div style={styles.secretBox}>
                  <span>{this.state.createdEndpointUrl}</span>
                  <IconButton size="small" onClick={() => this.copyToClipboard(this.state.createdEndpointUrl)} sx={{ color: '#8BC34A' }}>
                    <Copy size={16} />
                  </IconButton>
                </div>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px', marginTop: '16px' }}>{tr('appconnector.endpoints.dialog.created.secret.label')}</p>
                <div style={styles.secretBox}>
                  <span>{this.state.createdSecret}</span>
                  <IconButton size="small" onClick={() => this.copyToClipboard(this.state.createdSecret)} sx={{ color: '#8BC34A' }}>
                    <Copy size={16} />
                  </IconButton>
                </div>
              </DialogContent>
              <DialogActions>
                <Button variant="contained" onClick={() => this.setState({ secretDialogOpen: false })}>{tr('appconnector.endpoints.dialog.created.confirm')}</Button>
              </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => this.setState({ deleteDialogOpen: false })}>
              <DialogTitle>{tr('appconnector.common.confirm.delete')}</DialogTitle>
              <DialogContent>{tr('appconnector.endpoints.dialog.delete.confirm')}</DialogContent>
              <DialogActions>
                <Button onClick={() => this.setState({ deleteDialogOpen: false })}>{tr('appconnector.common.cancel')}</Button>
                <Button variant="contained" color="error" onClick={this.handleDelete}>{tr('appconnector.common.delete')}</Button>
              </DialogActions>
            </Dialog>

            <CustomizedSnackbars variant={this.state.notificationVariant} message={this.state.notificationMessage}
              isOpen={this.state.openNotification} toClose={this.closeNotification} />
          </Container>
        </main>
        <Footer />
      </div>
    );
  }
}

AppConnectorEndpoints.propTypes = { classes: PropTypes.object.isRequired };
const mapStateToProps = state => ({ actionsHeaderMenu: state.actionsHeaderMenu, headerMenu: state.headerMenu, userSession: state.userSession });
const mapDispatchToProps = dispatch => bindActionCreators(componentActions, dispatch);
export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(withStyles(Styles)(AppConnectorEndpoints)));
